import { Metric } from '@fullstory/proto/fs/services/search-settings';
import { Timestamp } from '@fullstory/proto/google/protobuf';
import { useFlags } from '@fullstory/react-hooks';
import {
  AppDispatch,
  AppState,
  ComputeMetricResponseWithId,
  DataTableWithId,
  MetricsActions,
  SegmentsById,
  fullstorySelectors,
  getSearchCacheKey,
  useAppCacheSwapCallback,
  useComputationSetStatus,
  useReleaseAlexaBackgroundRefs,
} from '@fullstory/redux';
import { getOrgId } from '@fullstory/redux/modules/fullstory/selectors';
import { MetricsAsyncActions } from '@fullstory/redux/modules/metrics/actions';
import { makeComputedGetMetricsDataArray } from '@fullstory/redux/modules/metrics/selectors';
import { getSegmentsById } from '@fullstory/redux/modules/segments/selectors';
import { isNonNull } from '@fullstory/typeutils';
import { hashObject } from '@fullstory/util/hash';
import { UID } from '@fullstory/util/uid';
import { useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { getSegmentArrayFromVizSettings, getVisualizationSettings } from '../utils';

/**
 * This hook is designed to perform a set of metric computations and will cover most
 * use-cases where we are simply visualizing the data.
 * @param computations - Metric computations to fetch
 * @param shouldFetch - If `false`, requests will not be dispatched. Useful for preventing
 * requests until some condition is met (e.g. the component is in view).
 * @param cacheOptions - Optionally pass in cache options for specific computations. Can be used to
 * improve performance of our DX modal grid view, which loads all funnels
 * @param forceSwap - If `true` (default), refreshed data will automatically be swapped into the fresh data.
 */
export const useMetricComputations = (
  definition: Metric | undefined | null,
  shouldFetch: boolean = true,
  storageId: string,
  forceSwap: boolean = true,
) => {
  const forceRefresh = useFetchMetricComputations(definition, shouldFetch, storageId, forceSwap);

  const getter = useMemo(() => makeComputedGetMetricsDataArray([storageId]), [storageId]);
  const responses = useSelector((state: AppState) => getter(state));
  const results: DataTableWithId[] = useMemo(
    () => responses.flatMap(({ data }) => data?.map(d => d.data)).filter(isNonNull),
    [responses],
  );

  const getTimestamp = useMemo(
    () => (data: ComputeMetricResponseWithId[] | null | undefined) =>
      data?.reduce((acc, response) => {
        const resDate = Timestamp.toDate(response.computedAt);
        return resDate && resDate > acc ? resDate : acc;
      }, new Date(0)) ?? null,
    [],
  );

  // get the status of the whole set of computations
  const computationSetStatus = useComputationSetStatus<ComputeMetricResponseWithId[]>(
    responses,
    getTimestamp,
  );

  const forceCacheSwap = useAppCacheSwapCallback<ComputeMetricResponseWithId[]>(
    responses,
    MetricsActions.appCacheSwap,
  );

  const orgId = useSelector(fullstorySelectors.getOrgId);
  const backgroundRefIds = useMemo(
    () =>
      responses.reduce<string[]>((acc, response) => {
        const ids = (response.data ?? []).map(d => d.backgroundRefId);
        return [...acc, ...ids];
      }, []),
    [responses],
  );
  useReleaseAlexaBackgroundRefs(orgId, backgroundRefIds, !shouldFetch);

  return useMemo(
    () => ({
      /**
       * Function that, when called, triggers a forced refresh of every
       * computation in the set
       */
      forceRefresh,
      /**
       * An array containing the `DataTable`s returned for each computation
       */
      results,
      /**
       * The raw responses for each computation, which includes the status of
       * each request (useful for more advanced use-cases, e.g. trends card
       * displays the status of each request in the chart legend)
       */
      responses,
      /**
       * Function that, when called, triggers a cache swap of every computation
       * in the set
       */
      forceCacheSwap,
      ...computationSetStatus,
    }),
    [computationSetStatus, forceCacheSwap, forceRefresh, responses, results],
  );
};

const useFetchMetricComputations = (
  definition: Metric | undefined | null,
  shouldFetch: boolean = true,
  storageId: string,
  forceSwap: boolean = false,
) => {
  const dispatch = useDispatch<AppDispatch>();
  const orgId = useSelector(getOrgId);
  const searchCacheKey = useSelector(getSearchCacheKey);
  const segmentsById = useSelector(getSegmentsById);
  const segmentCacheKey = useSegmentsHash(definition, segmentsById);
  const flags = useFlags();
  if (!flags) {
    throw new Error('No flags found, cannot search');
  }
  const fetchMetricComputations = useCallback(
    (isRefresh = false) => {
      const uid = UID();

      const request = {
        appCache: MetricsAsyncActions.appCache(uid, forceSwap),
        storageId,
        payload: {
          body: { orgId, definition: definition ?? null, isRefresh },
          flags,
          searchCacheKey,
          segmentCacheKey,
        },
        uid,
      };
      request.appCache.action(dispatch, request.storageId, request.payload, isRefresh);
      return request;
    },
    [orgId, definition, flags, storageId, searchCacheKey, segmentCacheKey, dispatch, forceSwap],
  );

  useEffect(() => {
    let request: ReturnType<typeof fetchMetricComputations>;
    if (shouldFetch) {
      request = fetchMetricComputations();
    }
    return () => {
      if (request) {
        request.appCache.cancelSingle(dispatch, request.uid);
      }
    };
  }, [dispatch, shouldFetch, fetchMetricComputations]);

  const forceRefresh = useCallback(() => {
    fetchMetricComputations(true);
  }, [fetchMetricComputations]);

  return forceRefresh;
};

export const getSegmentsHash = (metric: Metric | null | undefined, segmentsById: SegmentsById) => {
  const visSettings = getVisualizationSettings(metric);
  const metricSegments = getSegmentArrayFromVizSettings(visSettings?.segment);

  return hashObject(
    metricSegments.map(s => {
      if (s.segmentId) {
        const savedSegment = segmentsById[s.segmentId]?.segment;
        return { id: savedSegment?.segment?.id, version: savedSegment?.metadata?.version };
      } else if (s.segmentDefinition) {
        return s.segmentDefinition;
      } else if (s.namedSegmentDefinition) {
        return s.namedSegmentDefinition;
      }
      return {};
    }),
  );
};

// exposed for testing
export const useSegmentsHash = (
  metric: Metric | null | undefined,
  segmentsById: SegmentsById,
): number => useMemo(() => getSegmentsHash(metric, segmentsById), [metric, segmentsById]);
